import store from 'redux/store';
import API from 'services/api';
import {
  STATUSES,
  HOSTED_PAGE_TYPES,
  ERROR_500_PROPS,
  ERROR_404_PROPS,
  ERROR_500_MESSAGES,
  EXPIRED_PROPS,
  LIVENESS_CHECK_ERROR_CODES,
  SUCCESS_CODE
} from 'consts';
import { IDV_THANK_YOU, IDV_EXPIRED, IDV_DOCS } from 'routes/paths';
import { getRouteWithToken, redirectToError } from 'utilities';
import { sanitizeBase64, getMimeType } from 'utilities/idv';
import { sendGetPageFailMessage } from 'services/iframeService';
import history from 'shared/services/history';
import showErrorAlert, { parseError } from 'services/errorHandler';
import * as CONSTS from 'redux/constants';
import { Dispatch } from 'redux';
import { OptionValue } from 'components/core/Dropdown/Dropdown';
import { displaySpinner, hideSpinner } from './global';

interface IIdvPage {
  token: string;
  isThankYou?: boolean;
}

interface IError {
  error: Record<string, any> | string;
}

interface ISpinner {
  meta: {
    pageType: string;
    notifySdk: boolean;
  };
}

const redirectToPage = (params: {
  page: string;
  isThankYou: boolean;
  isSdk: boolean;
}) => {
  const { isSdk, isThankYou, page } = params;
  if (isThankYou || isSdk) return;
  history.push(page);
};

export const getIdvPageStart = () => ({
  type: CONSTS.GET_IDV_PAGE_START
});

export const getIdvPageSuccess = (data: Record<string, any>) => ({
  type: CONSTS.GET_IDV_PAGE_SUCCESS,
  payload: { data }
});

export const getIdvPageFail = (error: string, data: Record<string, any>) => ({
  type: CONSTS.GET_IDV_PAGE_FAIL,
  payload: { error, data }
});

export const setAllIdvDataFetched = (allFetched: boolean) => ({
  type: CONSTS.SET_ALL_IDV_DATA_FETCHED,
  payload: allFetched
});

export const getIdvPage =
  ({ token, isThankYou = false }: IIdvPage) =>
  async (dispatch: Dispatch<any>, getState: () => Record<string, any>) => {
    const handleGetPageError = (error: Record<string, any>) => {
      const { status, data } = parseError(error);
      const { error_code: errorCode } = status || {};
      dispatch(getIdvPageFail(errorCode, data || {}));

      const errorProps =
        error?.response?.data?.status?.error_code === ERROR_500_MESSAGES.PAGE_EXPIRED
          ? EXPIRED_PROPS
          : ERROR_500_PROPS;
      redirectToError({ error, IDV_EXPIRED, ...errorProps });
    };

    if (!token) {
      sendGetPageFailMessage(HOSTED_PAGE_TYPES.idv)();
      return redirectToError({ ...ERROR_404_PROPS });
    }

    try {
      const { isSdk } = getState().global;

      dispatch(displaySpinner());
      dispatch(getIdvPageStart());

      let page;
      try {
        page = await API.get(`hosted/idv/${token}`);
      } catch (error) {
        return handleGetPageError(error as Record<string, any>);
      }

      const { data: idvData } = page.data;
      dispatch(getIdvPageSuccess(idvData));

      if (idvData?.status === STATUSES.done) {
        return redirectToPage({
          page: getRouteWithToken(IDV_THANK_YOU, token),
          isThankYou,
          isSdk
        });
      }

      const { country, document_type: documentType } = idvData;
      if (country) {
        const documents = await dispatch(
          getIdvDocuments(token, country, documentType && [documentType]) as any
        );
        if (documentType && documents?.length) {
          redirectToPage({ page: getRouteWithToken(IDV_DOCS, token), isThankYou, isSdk });
        }
      }

      dispatch(setAllIdvDataFetched(true));
    } catch (error) {
      showErrorAlert({ error } as IError);
      handleGetPageError(error as Record<string, any>);
    } finally {
      dispatch(hideSpinner({ meta: { pageType: HOSTED_PAGE_TYPES.idv } } as ISpinner));
    }
  };

export const getIdvDocumentsStart = () => ({
  type: CONSTS.GET_IDV_DOCUMENTS_START
});

export const getIdvDocumentsSuccess = (data: Record<string, any>) => ({
  type: CONSTS.GET_IDV_DOCUMENTS_SUCCESS,
  payload: { data }
});

export const getIdvDocumentsFail = (error: string) => ({
  type: CONSTS.GET_IDV_DOCUMENTS_FAIL,
  payload: { error }
});

export const clearIdvDocuments = () => ({
  type: CONSTS.RESET_IDV_DOCUMENTS
});

export const getIdvDocuments =
  (token: string, country: string, documentsToInclude?: Array<string>) =>
  async (dispatch: Dispatch<any>) => {
    dispatch(getIdvDocumentsStart());
    try {
      const suffix = country ? `?country=${country}` : '';
      const docsResponse = await API.get(`hosted/idv/${token}/doc_types${suffix}`);

      const docs = docsResponse.data.data;
      const filteredDocs = documentsToInclude?.length
        ? docs.filter((doc: Record<string, any>) => documentsToInclude.includes(doc.type))
        : docs;
      dispatch(getIdvDocumentsSuccess(filteredDocs));
      return filteredDocs;
    } catch (error) {
      showErrorAlert({ error });
      const { status } = parseError(error);
      const { error_code: errorCode } = status || {};
      dispatch(getIdvDocumentsFail(errorCode));
    }
  };

export const verifyDocsStart = () => ({
  type: CONSTS.VERIFY_DOCUMENTS_START
});

export const verifyDocsSuccess = (data: Record<string, any>) => ({
  type: CONSTS.VERIFY_DOCUMENTS_SUCCESS,
  payload: { data }
});

export const verifyDocsFail = (error: string) => ({
  type: CONSTS.VERIFY_DOCUMENTS_FAIL,
  payload: { error }
});

type MimeType = string | null;
let mimeType: MimeType;
let backSideImage: string;

export const verifyDocs =
  (
    token: string,
    country: string,
    docType: string,
    isBackRequired: boolean,
    images: Record<string, any>,
    onSuccess: () => void,
    onFail: () => void
  ) =>
  async (dispatch: Dispatch<any>, getState: () => Record<string, any>) => {
    try {
      const { isPreview } = getState().global;
      if (isPreview) return;

      dispatch(verifyDocsStart());

      if (isBackRequired) {
        mimeType = getMimeType(images.back);
        backSideImage = sanitizeBase64(images.back);
      }

      const body = {
        country,
        document_type: docType,
        face_image: sanitizeBase64(images.selfie),
        face_image_mime_type: getMimeType(images.selfie),
        front_side_image: sanitizeBase64(images.front),
        front_side_image_mime_type: getMimeType(images.front),
        back_side_image: backSideImage,
        back_side_image_mime_type: mimeType
      };

      const result = await API.post(`hosted/idv/${token}/verify`, body);

      dispatch(verifyDocsSuccess(result.data.data));
      if (onSuccess) onSuccess();
    } catch (error) {
      if (onFail) onFail();
      showErrorAlert({ error });
      const { status } = parseError(error);
      const { error_code: errorCode } = status || {};
      dispatch(verifyDocsFail(errorCode));
    }
  };

export const livenessCheckStart = () => ({
  type: CONSTS.LIVENESS_CHECK_START
});

export const livenessCheckSuccess = (data: Record<string, any>) => ({
  type: CONSTS.LIVENESS_CHECK_SUCCESS,
  payload: { data }
});

export const livenessCheckFail = (error: string) => ({
  type: CONSTS.LIVENESS_CHECK_FAIL,
  payload: { error }
});

export const livenessCheck =
  (token: string, selfieImage: string, onSuccess: () => void) =>
  async (dispatch: Dispatch<any>) => {
    try {
      const state = store?.getState() || {};
      const { isPreview } = state.global || {};
      if (isPreview) return;

      dispatch(livenessCheckStart());
      const body = {
        face_image: sanitizeBase64(selfieImage),
        face_image_mime_type: getMimeType(selfieImage)
      };

      const result = await API.post(`hosted/idv/${token}/liveness_check`, body);
      const { data } = result?.data || {};
      if (data) {
        dispatch(livenessCheckSuccess(data));
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      const { status } = parseError(error);
      const { error_code: errorCode } = status || {};
      if (LIVENESS_CHECK_ERROR_CODES.includes(errorCode)) {
        showErrorAlert({ error, errorTitleKey: 'idv.liveness_check_errors.title' });
        dispatch(livenessCheckFail(errorCode));
      } else {
        dispatch(livenessCheckSuccess({ status: { status: SUCCESS_CODE } }));
        if (onSuccess) onSuccess();
      }
    }
  };

export const setSelectedCountry = (value: OptionValue) => ({
  type: CONSTS.SET_SELECTED_COUNTRY,
  payload: value
});

export const verifySecureMeFlowStart = () => ({
  type: CONSTS.VERIFY_SECUREME_FLOW_START
});

export const verifySecureMeFlowSuccess = (data: Record<string, any>) => ({
  type: CONSTS.VERIFY_SECUREME_FLOW_SUCCESS,
  payload: { data }
});

export const verifySecureMeFlowFail = (error: string) => ({
  type: CONSTS.VERIFY_SECUREME_FLOW_FAIL,
  payload: { error }
});

export const verifySecureMeFlow =
  (token: string) =>
  async (dispatch: Dispatch<any>, getState: () => Record<string, any>) => {
    try {
      const { isPreview, isSdk } = getState().global || {};
      if (isPreview) return;
      dispatch(verifySecureMeFlowStart());
      const result = await API.post(`hosted/idv/${token}/verify_secure`, {});
      const {
        status: { status }
      } = result.data || {};
      if (status === SUCCESS_CODE) {
        dispatch(verifySecureMeFlowSuccess({}));
        if (isSdk) return;
        return history.push(getRouteWithToken(IDV_THANK_YOU, token));
      }
    } catch (error) {
      showErrorAlert({ error });
      const { status } = parseError(error);
      const { error_code: errorCode } = status || {};
      dispatch(verifySecureMeFlowFail(errorCode));
    }
  };
